import { faTooth } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';

import { useIsBasicOdontogram } from '@/modules/patientList/patientStrip/patientStripOdontogram/hooks';
import {
  getRecommendedProcedures,
  useGetRecommendationName,
} from '@/modules/patientList/patientStrip/patientStripOdontogram/utils';
import { usePreferences } from '@/react-query/usePreferences';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { getSeverityFromColor } from '@/utils/hooks/useRblSeverities';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';

import { SeverityTag } from '../../visitSidebar/SeverityTag';
import { ToothDamageProgress } from '../../visitSidebar/ToothDamageProgress';

interface Props {
  isMobile: boolean;
  isPerioPatient: boolean;
  visitSummaryTooth: TVisitSummary['teeth'][number];
}

export const PatientPrintoutTooth = ({
  isMobile,
  isPerioPatient,
  visitSummaryTooth,
}: Props) => {
  const isBasicOdontogram = useIsBasicOdontogram();
  const { displayToothNumber } = useUniversalToFDI();
  const { data: preferences } = usePreferences();
  const { getRecommendationName } = useGetRecommendationName();

  const recommendedProcedures = getRecommendedProcedures(
    visitSummaryTooth,
    preferences,
    true,
    isPerioPatient,
  );

  const getIndicatorBgColor = () => {
    const dmfPercent = visitSummaryTooth.dmf ? visitSummaryTooth.dmf * 100 : 0;
    if (dmfPercent <= 5) return 'vai-bg-[#D4A700]';
    if (dmfPercent <= 35) return 'vai-bg-orange-500';
    if (dmfPercent <= 65) return 'vai-bg-rose-600';
    return 'vai-bg-pink-600';
  };

  const combinedRblSeverity = getSeverityFromColor(
    visitSummaryTooth.rblSeverity ?? undefined,
  );

  if (
    !recommendedProcedures.length &&
    !combinedRblSeverity &&
    !visitSummaryTooth.dmf
  ) {
    return null;
  }

  const showRecommendations =
    !!recommendedProcedures.length && !isBasicOdontogram;

  return (
    <>
      <div
        className="vai-break-inside-avoid"
        data-testid={`patient-printout-tooth-${visitSummaryTooth.toothId}`}
      >
        <div
          data-testid="patient-printout-tooth-header"
          className={classNames('vai-flex vai-items-center vai-gap-2', {
            'vai-mb-2': isMobile,
            'vai-mb-4': !isMobile,
          })}
        >
          <FontAwesomeIcon icon={faTooth} size={isMobile ? 'sm' : 'lg'} />
          <p
            className={classNames('vai-font-inter vai-font-medium', {
              'vai-text-lg': !isMobile,
              'vai-text-base': isMobile,
            })}
          >
            <Trans>Tooth</Trans> {displayToothNumber(visitSummaryTooth.toothId)}
          </p>
        </div>

        {showRecommendations && (
          <div
            className={classNames('vai-flex', {
              'vai-flex-row vai-items-center vai-gap-2 vai-mb-2': isMobile,
              'vai-flex-col vai-gap-3 vai-mb-4': !isMobile,
            })}
          >
            <p className="vai-font-inter vai-text-xs vai-text-slate-600 vai-uppercase">
              <Trans>Recommendation</Trans>
            </p>
            <p className="vai-font-inter vai-text-sm vai-font-medium">
              {recommendedProcedures
                .map(({ name }) => getRecommendationName(name))
                .join(' & ')}
            </p>
          </div>
        )}

        {(combinedRblSeverity || !!visitSummaryTooth.dmf) && (
          <div
            className={classNames(
              'vai-grid vai-grid-cols-[auto_1fr] vai-gap-y-2',
              { 'vai-gap-x-4': isMobile, 'vai-gap-x-8': !isMobile },
            )}
          >
            <p className="vai-font-inter vai-text-[12px] vai-uppercase vai-text-slate-600">
              <Trans>Bone Level</Trans>
            </p>
            <p className="vai-font-inter vai-text-[12px] vai-uppercase vai-text-slate-600">
              <Trans>Tooth Damage</Trans>
            </p>

            <div className="vai-flex vai-items-start">
              <SeverityTag
                hideTooltip
                isPatientPrintout
                severity={combinedRblSeverity ?? 'unknown'}
              />
            </div>

            {visitSummaryTooth.dmf ? (
              <ToothDamageProgress
                className="vai-bg-zinc-200"
                dmf={visitSummaryTooth.dmf}
                indicatorClassName={getIndicatorBgColor()}
                minimumValue={13}
              />
            ) : (
              <>
                <p className="vai-font-inter vai-text-[12px] vai-font-medium vai-text-zinc-500">
                  {typeof visitSummaryTooth.dmf === 'number' ? (
                    <Trans>No tooth damage detected.</Trans>
                  ) : (
                    <Trans>No measurement available.</Trans>
                  )}
                </p>
              </>
            )}
          </div>
        )}
      </div>
      <hr className="vai-my-4 vai-border-slate-200" />
    </>
  );
};
